import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Eye, Users, MapPin, FileText, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getAnalytics } from '../../lib/api';
import { toast } from 'sonner';

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'custom', label: 'Custom' },
];

export default function AnalyticsTab() {
  const [period, setPeriod] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom' && customStart && customEnd) {
        params.start = new Date(customStart).toISOString();
        params.end = new Date(customEnd).toISOString();
      }
      const res = await getAnalytics(params);
      setData(res.data);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => {
    if (period !== 'custom' || (customStart && customEnd)) {
      fetchAnalytics();
    }
  }, [fetchAnalytics, period, customStart, customEnd]);

  return (
    <div data-testid="analytics-tab">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em]">Analytics</h1>
        <div className="flex items-center gap-1.5 flex-wrap">
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                period === p.value
                  ? 'border-[#FF6B00]/30 bg-[#FF6B00]/10 text-[#FF6B00]'
                  : 'border-white/10 bg-white/[0.03] text-white/55 hover:bg-white/[0.06]'
              }`}
              data-testid={`analytics-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {period === 'custom' && (
        <div className="flex items-center gap-3 mb-6" data-testid="analytics-custom-range">
          <input
            type="date"
            value={customStart}
            onChange={e => setCustomStart(e.target.value)}
            className="px-3 py-2 rounded-md border border-white/10 bg-black/40 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6B00] [color-scheme:dark]"
            data-testid="analytics-custom-start"
          />
          <span className="text-xs text-white/40">to</span>
          <input
            type="date"
            value={customEnd}
            onChange={e => setCustomEnd(e.target.value)}
            className="px-3 py-2 rounded-md border border-white/10 bg-black/40 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6B00] [color-scheme:dark]"
            data-testid="analytics-custom-end"
          />
          <Button
            onClick={fetchAnalytics}
            disabled={!customStart || !customEnd}
            size="sm"
            className="bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 text-xs disabled:opacity-50"
            data-testid="analytics-apply-custom"
          >
            Apply
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-5 w-5 animate-spin text-white/30" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard icon={Eye} label="Total Views" value={data.total_views} />
            <SummaryCard icon={Users} label="Unique Visitors" value={data.unique_visitors} accent />
            <SummaryCard icon={FileText} label="Top Pages" value={data.top_pages?.length || 0} />
            <SummaryCard icon={MapPin} label="Locations" value={data.locations?.length || 0} />
          </div>

          {/* Chart */}
          <div className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-[#FF6B00]" />
              <h3 className="text-sm font-semibold text-white/80">Daily Traffic</h3>
            </div>
            {data.daily && data.daily.length > 0 ? (
              <div className="h-[280px]" data-testid="analytics-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.daily} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <defs>
                      <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF6B00" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FF6B00" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="uniqueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.35)' }}
                      tickFormatter={d => {
                        const parts = d.split('-');
                        return `${parts[1]}/${parts[2]}`;
                      }}
                      stroke="rgba(255,255,255,0.08)"
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.35)' }}
                      stroke="rgba(255,255,255,0.08)"
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#0F1419',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        fontSize: 12,
                        color: '#fff',
                      }}
                      labelFormatter={d => `Date: ${d}`}
                    />
                    <Area type="monotone" dataKey="views" name="Views" stroke="#FF6B00" fill="url(#viewsGrad)" strokeWidth={2} />
                    <Area type="monotone" dataKey="unique" name="Unique" stroke="#818cf8" fill="url(#uniqueGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-12 text-white/30 text-sm">No traffic data for this period.</div>
            )}
          </div>

          {/* Top Pages & Locations side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Pages */}
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-4 w-4 text-[#FF6B00]" />
                <h3 className="text-sm font-semibold text-white/80">Top Pages</h3>
              </div>
              <div className="space-y-2" data-testid="analytics-top-pages">
                {data.top_pages?.length > 0 ? data.top_pages.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-sm text-white/70 font-mono truncate flex-1 mr-3">{p.page}</span>
                    <Badge className="bg-white/8 text-white/60 border-white/12 text-[10px] font-mono">{p.views}</Badge>
                  </div>
                )) : (
                  <p className="text-xs text-white/30 py-4 text-center">No page data yet.</p>
                )}
              </div>
            </div>

            {/* Locations */}
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-4 w-4 text-[#FF6B00]" />
                <h3 className="text-sm font-semibold text-white/80">Viewer Locations</h3>
              </div>
              <div className="space-y-2" data-testid="analytics-locations">
                {data.locations?.length > 0 ? data.locations.map((loc, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div className="flex-1 min-w-0 mr-3">
                      <span className="text-sm text-white/70">{loc.country}</span>
                      {loc.city && <span className="text-xs text-white/40 ml-2">{loc.city}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge className="bg-[#FF6B00]/10 text-[#FF6B00] border-[#FF6B00]/20 text-[10px] font-mono">{loc.views} views</Badge>
                      <Badge className="bg-white/8 text-white/50 border-white/12 text-[10px] font-mono">{loc.unique} unique</Badge>
                    </div>
                  </div>
                )) : (
                  <p className="text-xs text-white/30 py-4 text-center">No location data yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-[14px] border border-white/10 bg-white/[0.03] p-5" data-testid={`analytics-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${accent ? 'text-[#FF6B00]' : 'text-white/40'}`} />
        <span className="text-xs text-white/55">{label}</span>
      </div>
      <div className={`text-3xl font-semibold font-mono ${accent ? 'text-[#FF6B00]' : 'text-white'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
